import { Box, IconButton, Slider, Typography } from '@mui/material';
import { useRef, useState, useCallback, useEffect, useContext } from 'react';
import ReactPlayer from 'react-player';
import { useAuth } from '../../context/AuthContext';
import { useCourses } from '../../context/CourseContext';
import { userAxios } from '../../utils/api';
import { logger } from '../../utils/SiteHelpers';
import { Pause, PlayArrow, VolumeUp } from '@mui/icons-material';
import { UserSettingsContext } from '../../context/UserSettingsContext';

const VideoPlayer = ({
    course,
    videoUrl,
    onComplete,
    isCompleted,
    courseId,
    videoControls,
    courseItemId,
}) => {
    const playerRef = useRef(null);
    const [hasWatched, setHasWatched] = useState(isCompleted);
    const [apiCallSent, setApiCallSent] = useState(false);
    const { updateCourseProgress } = useCourses();
    const [maxPlayedSeconds, setMaxPlayedSeconds] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [played, setPlayed] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [volume, setVolume] = useState(0.8);
    const [muted, setMuted] = useState(false);
    const [seeking, setSeeking] = useState(false);
    const { userSettings } = useContext(UserSettingsContext);
    const pathSegments = window.location.pathname.split('/');
    const lastPathValue = pathSegments[pathSegments.length - 1];

    // Conditionally override courseItemId
    const effectiveCourseItemId =
        lastPathValue === 'welcome' ? 2 : courseItemId;

    const handleProgress = useCallback(
        (state) => {
            if (!seeking) {
                setPlayed(state.played);
                setCurrentTime(state.playedSeconds);
            }

            if (state.playedSeconds > maxPlayedSeconds) {
                setMaxPlayedSeconds(state.playedSeconds);
            }
        },
        [
            seeking,
            maxPlayedSeconds,
            hasWatched,
            apiCallSent,
            updateCourseProgress,
        ],
    );

    useEffect(() => {
        if (!course) return;
        if (
            course.is_items_controls === 0 ||
            userSettings?.webinar_audit_control === 0
        ) {
            updateCourseProgress(
                courseId,
                effectiveCourseItemId,
                effectiveCourseItemId === 2 ? 'welcome' : 'video',
            );
        }
    }, []);

    const handleSeekMouseDown = () => {
        setSeeking(true);
    };
    const handleSeekChange = (event, newValue) => {
        const seekTime = (newValue / 100) * duration;

        // If controls are enabled, restrict all seeking
        if (videoControls && !hasWatched) return;

        setPlayed(newValue / 100);
        setCurrentTime(seekTime);
    };
    const handleSeekMouseUp = (event, newValue) => {
        setSeeking(false);
        const seekTime = (newValue / 100) * duration;

        if (videoControls && !hasWatched) {
            // Restrict all seeking
            playerRef.current?.seekTo(maxPlayedSeconds, 'seconds');
            return;
        }

        // Allow free seeking if controls are disabled
        playerRef.current?.seekTo(newValue / 100, 'fraction');
    };

    const handlePlayPause = () => {
        setPlaying(!playing);
    };

    const handleVolumeChange = (event, newValue) => {
        setVolume(newValue / 100);
        setMuted(newValue === 0);
    };

    const handleMute = () => {
        setMuted(!muted);
    };

    const formatTime = (seconds) => {
        const date = new Date(seconds * 1000);
        const hh = date.getUTCHours();
        const mm = date.getUTCMinutes();
        const ss = date.getUTCSeconds().toString().padStart(2, '0');
        if (hh) {
            return `${hh}:${mm.toString().padStart(2, '0')}:${ss}`;
        }
        return `${mm}:${ss}`;
    };

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                aspectRatio: '16/9',
            }}
        >
            <ReactPlayer
                ref={playerRef}
                url={videoUrl}
                width="100%"
                height="100%"
                playing={playing}
                volume={muted ? 0 : volume}
                controls={false} // Hide default controls
                onProgress={handleProgress}
                onDuration={setDuration}
                onEnded={() => {
                    setHasWatched(true);
                    updateCourseProgress(
                        courseId,
                        effectiveCourseItemId,
                        effectiveCourseItemId === 2 ? 'welcome' : 'video',
                    );
                    onComplete();
                }}
                config={{
                    youtube: {
                        playerVars: {
                            rel: 0,
                            modestbranding: 1,
                            controls: 0, // Hide YouTube controls
                            disablekb: 1,
                            fs: 0, // Disable YouTube fullscreen
                            iv_load_policy: 3,
                            showinfo: 0,
                        },
                    },
                }}
                playsinline={true}
            />
            // {/* Click overlay for play/pause */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: '80px', // Leave space for controls
                    cursor: 'pointer',
                    zIndex: 1,
                }}
                onClick={handlePlayPause}
            />
            {/* Custom Controls Overlay - Always Visible */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                    padding: '20px 16px 16px',
                    zIndex: 2,
                }}
            >
                {/* Progress Bar */}
                <Box sx={{ position: 'relative' }}>
                    <Slider
                        value={played * 100}
                        onChange={handleSeekChange}
                        onMouseDown={handleSeekMouseDown}
                        onChangeCommitted={handleSeekMouseUp}
                        sx={{
                            color: '#ff0000',
                            height: 6,
                            '& .MuiSlider-thumb': {
                                width: 16,
                                height: 16,
                                backgroundColor: '#ff0000',
                                '&:hover, &.Mui-focusVisible': {
                                    boxShadow:
                                        '0 0 0 8px rgba(255, 0, 0, 0.16)',
                                },
                                '&:before': {
                                    display: 'none',
                                },
                            },
                            '& .MuiSlider-track': {
                                border: 'none',
                                backgroundColor: '#ff0000',
                                height: 6,
                            },
                            '& .MuiSlider-rail': {
                                backgroundColor: 'black',
                                height: 6,
                            },
                        }}
                    />
                </Box>

                {/* Control Bar */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: 'white',
                    }}
                >
                    {/* Left Controls */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Play/Pause Button */}
                        <IconButton
                            onClick={handlePlayPause}
                            sx={{
                                color: 'white',
                                p: 1,
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                },
                            }}
                        >
                            {playing ? (
                                <Pause sx={{ fontSize: 28 }} />
                            ) : (
                                <PlayArrow sx={{ fontSize: 28 }} />
                            )}
                        </IconButton>

                        {/* Volume Button */}
                        <IconButton
                            onClick={handleMute}
                            sx={{
                                color: 'white',
                                p: 1,
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                },
                            }}
                        >
                            <VolumeUp sx={{ fontSize: 24 }} />
                        </IconButton>

                        {/* Volume Slider */}
                        <Box sx={{ width: 80, mx: 1 }}>
                            <Slider
                                value={muted ? 0 : volume * 100}
                                onChange={handleVolumeChange}
                                size="small"
                                sx={{
                                    color: 'white',
                                    height: 4,
                                    '& .MuiSlider-thumb': {
                                        width: 12,
                                        height: 12,
                                        backgroundColor: 'white',
                                    },
                                    '& .MuiSlider-track': {
                                        backgroundColor: 'white',
                                    },
                                    '& .MuiSlider-rail': {
                                        backgroundColor:
                                            'rgba(255, 255, 255, 0.3)',
                                    },
                                }}
                            />
                        </Box>

                        {/* Time Display */}
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'white',
                                minWidth: '120px',
                                fontSize: '14px',
                            }}
                        >
                            {formatTime(currentTime)} / {formatTime(duration)}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default VideoPlayer;
